/*
 * Stage 1 + Stage 2 API client — v2.7 Solution Options + Bypass.
 *
 * Endpoints:
 *   GET  /api/intake/stage1/:engagementId        return 3 cards
 *   POST /api/intake/stage1/pick                 user picks one, get outcome
 */

import axios from 'axios';

import type { RoutePlan } from './dossier-types';


const API_BASE = process.env.REACT_APP_API_URL ?? '';

function url(path: string): string {
  return `${API_BASE}${path}`;
}


export type SolutionOptionKind = 'marketplace' | 'template' | 'custom';

export interface SolutionOptionCard {
  kind: SolutionOptionKind;
  solution_id: string | null;
  solution_slug: string | null;
  name: string;
  tagline: string;
  fit_score: number;
  tool_compat_score: number;
  cust_needed_score: number;
  solves: string[];
  misses: string[];
  price_summary: string;
  timeline_summary: string;
  ip_terms: string;
  compliance: string;
  rationale: string[];
  landing_url: string | null;
}

export interface Stage1Response {
  engagement_id: string;
  eligible: boolean;
  ineligible_reason: string | null;
  cards: SolutionOptionCard[];
  marketplace_match_count: number;
}

export type BypassOutcome =
  | 'direct_deploy'
  | 'template_direct'
  | 'planner_direct'
  | 'l2_required';

export interface BypassDecision {
  pick: SolutionOptionKind;
  clarity_score: number;
  threshold: number;
  bypass_eligible: boolean;
  outcome: BypassOutcome;
  outcome_label: string;
  next_route: string;
  hard_gates: string[];
  component_scores: Record<string, number>;
  reason: string;
}

export interface Stage1PickResponse {
  decision: BypassDecision;
  route_plan: RoutePlan | null;
}


export async function fetchStage1Options(
  engagementId: string,
): Promise<Stage1Response> {
  const res = await axios.get<Stage1Response>(
    url(`/api/intake/stage1/${engagementId}`),
  );
  return res.data;
}


export async function pickStage1Option(
  engagementId: string,
  pick: SolutionOptionKind,
  solutionSlug?: string | null,
): Promise<Stage1PickResponse> {
  const res = await axios.post<Stage1PickResponse>(
    url('/api/intake/stage1/pick'),
    { engagement_id: engagementId, pick, solution_slug: solutionSlug ?? null },
  );
  return res.data;
}
